import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Pagination } from '../../ds/components/Pagination';
import { useWhenMounted } from '../useWhenMounted';
/** Doc: {@link http://localhost:3000/playground?doc=useFetch | Playground - useFetch}. */
export function useFetch({ disabled, recursive = false, throwError = false, fetchParams, debounceTime = 500, recursiveTime = 2000, fetchOnRender = false, paginationParams, onError, onSuccess, onAfterFetch, onBeforeFetch, recursiveTill, fetchFunction, onRecursiveStops, handleModifyParams, handleModifyResponse, }, deps = [], debouncedDeps = []) {
    const isMounted = useRef(false);
    const controllingPagination = !!paginationParams?.size;
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(paginationParams?.initialPage || 0);
    const fetch = useCallback(async (cbParams, lastResponse) => {
        const stopRecursive = recursiveTill?.(lastResponse);
        if (disabled || stopRecursive) {
            if (stopRecursive) {
                onRecursiveStops?.(lastResponse);
                setLoading(false);
            }
            return data;
        }
        try {
            setLoading(true);
            setError(null);
            await onBeforeFetch?.();
            let params = handleModifyParams?.(cbParams || fetchParams || {}) || cbParams || fetchParams;
            if (controllingPagination) {
                if (!params)
                    params = {};
                params.size = paginationParams.size;
                params.page = page;
            }
            const originalResponse = await fetchFunction(params);
            const modifiedResponse = await handleModifyResponse?.(originalResponse);
            const response = modifiedResponse || originalResponse;
            setData(response);
            onSuccess?.(response);
            if (recursive)
                setTimeout(() => fetch(params, response), recursiveTime);
            return response;
        }
        catch (apiError) {
            setError(apiError);
            onError?.(apiError);
            if (throwError)
                throw apiError;
            return undefined;
        }
        finally {
            onAfterFetch?.();
            if (!recursive || stopRecursive)
                setLoading(false);
        }
    }, [fetchParams, page]);
    useEffect(() => {
        if (!fetchOnRender)
            return;
        if (!isMounted && !fetchOnRender)
            return;
        fetch();
    }, []);
    useWhenMounted(() => {
        fetch();
    }, [page]);
    useWhenMounted(() => {
        if (!fetchOnRender)
            return;
        if (controllingPagination && page)
            return setPage(0);
        return fetch();
    }, deps);
    useEffect(() => {
        if (!fetchOnRender)
            return;
        if (isMounted.current) {
            let timer = null;
            timer = setTimeout(() => {
                fetch();
            }, debounceTime);
            return () => clearTimeout(timer);
        }
        isMounted.current = true;
        return () => { };
    }, debouncedDeps);
    const paginationProps = {
        page,
        waiting: loading,
        disabled,
        totalPages: data?.totalPages || 0,
        onPaginate: setPage,
    };
    return {
        data,
        page,
        error,
        loading,
        Pagination: paginationProps.totalPages > 1 ? _jsx(Pagination, { ...paginationProps }) : undefined,
        fetch,
        setPage,
    };
}
//# sourceMappingURL=index.js.map